// @ts-check
// Detalhe de uma cobrança do Asaas no painel de suporte: dados da cobrança e do cliente, notas fiscais ligadas a ela
// e as ações (estornar total ou parcial, remover cobrança em aberto, agendar ou emitir a nota).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { deleteAdminPayment, fetchAdminPayment, refundAdminPayment } from '../../../data/admin-finance.js';
import { canDeletePayment, canInvoicePayment, canRefundPayment } from '../../../lib/admin-navigation.js';
import { Button } from '../../../ui/button.js';
import { Modal } from '../../../ui/modal.js';
import { Skeleton } from '../../../ui/feedback.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { FormError, TextField } from '../../settings/integration-ui.js';
import { operationsErrorMessage, showOperationsError } from '../operations-format.js';
import { DefinitionList, ExternalLink, InvoiceStatusTag, OwnerLink, PaymentStatusTag, billingTypeLabel, formatDay, formatMoney, formatTaxDocument } from './admin-ui.js';
import { ScheduleInvoiceDialog } from './invoice-dialogs.js';
import { t } from '../../../i18n/index.js';

/** @param {string} key @param {Record<string, unknown>} [values] */
const text = (key, values) => t(`operations.admin.panel.payments.${key}`, values);

/**
 * @param {{ paymentId: string, onClose: () => void, onChanged: () => void }} props
 */
export function PaymentDetailDialog({ paymentId, onClose, onChanged }) {
  const [detail, setDetail] = useState(/** @type {any} */ (null));
  const [mode, setMode] = useState(/** @type {'view' | 'refund' | 'delete' | 'invoice'} */ ('view'));
  const [refundValue, setRefundValue] = useState('');
  const [refundDescription, setRefundDescription] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isWorking, setIsWorking] = useState(false);

  const load = () => fetchAdminPayment(paymentId).then(setDetail).catch((requestError) => {
    showOperationsError(requestError);
    onClose();
  });
  useEffect(() => {
    load();
  }, [paymentId]);

  const refund = async () => {
    setIsWorking(true);
    setError(null);
    try {
      const value = refundValue.trim() ? Number(refundValue.replace(',', '.')) : undefined;
      await refundAdminPayment(paymentId, { ...(value !== undefined ? { value } : {}), ...(refundDescription.trim() ? { description: refundDescription.trim() } : {}) });
      showSuccessSnackbar(text('refunded'));
      setMode('view');
      await load();
      onChanged();
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
    } finally {
      setIsWorking(false);
    }
  };

  const remove = async () => {
    setIsWorking(true);
    setError(null);
    try {
      await deleteAdminPayment(paymentId);
      showSuccessSnackbar(text('deleted'));
      onChanged();
      onClose();
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
      setIsWorking(false);
    }
  };

  if (mode === 'invoice' && detail) {
    return html`<${ScheduleInvoiceDialog} payment=${detail.payment} onClose=${() => setMode('view')} onScheduled=${() => {
      setMode('view');
      load();
      onChanged();
    }} />`;
  }
  if (mode === 'refund' && detail) {
    return html`<${Modal} title=${text('refundTitle')} subtitle=${text('refundSubtitle', { value: formatMoney(detail.payment.value) })} confirmLabel=${text('refundConfirm')} confirmAccent="danger"
      isLoading=${isWorking} onClose=${() => setMode('view')} onConfirm=${refund}>
      <div class="admin-form">
        <${TextField} label=${text('refundValue')} value=${refundValue} onInput=${setRefundValue} hint=${text('refundValueHint')} placeholder=${String(detail.payment.value)} />
        <${TextField} label=${text('refundDescription')} value=${refundDescription} onInput=${setRefundDescription} />
        <${FormError} message=${error} />
      </div>
    </${Modal}>`;
  }
  if (mode === 'delete' && detail) {
    return html`<${Modal} title=${text('deleteTitle')} subtitle=${text('deleteSubtitle', { value: formatMoney(detail.payment.value) })} confirmLabel=${text('deleteConfirm')} confirmAccent="danger"
      isLoading=${isWorking} onClose=${() => setMode('view')} onConfirm=${remove}>
      <${FormError} message=${error} />
    </${Modal}>`;
  }

  const payment = detail?.payment;
  const customer = detail?.customer;
  return html`<${Modal} title=${text('detailTitle')} subtitle=${payment ? `${payment.id}` : undefined} confirmLabel=${t('common.close')} hideCancel=${true} onClose=${onClose} onConfirm=${onClose}>
    ${!detail ? html`<${Skeleton} height=${200} radius=${8} />` : html`
      <div class="admin-dialog-body">
        <div class="admin-dialog-headline">
          <span class="admin-dialog-amount">${formatMoney(payment.value)}</span>
          <${PaymentStatusTag} status=${payment.status} />
        </div>
        <${DefinitionList} columns=${2} items=${[
          { label: text('owner'), value: html`<${OwnerLink} owner=${payment.owner} />` },
          { label: text('billingType'), value: billingTypeLabel(payment.billingType) },
          { label: text('dueDate'), value: formatDay(payment.dueDate) },
          { label: text('paymentDate'), value: formatDay(payment.clientPaymentDate ?? payment.paymentDate) },
          { label: text('netValue'), value: formatMoney(payment.netValue) },
          { label: text('invoiceNumber'), value: payment.invoiceNumber ?? '-', mono: true },
          { label: text('description'), value: payment.description || '-' },
          { label: text('externalReference'), value: payment.externalReference || '-', mono: true },
        ]} />
        <div class="admin-link-row">
          <${ExternalLink} href=${payment.invoiceUrl} label=${text('openInvoiceUrl')} />
          <${ExternalLink} href=${payment.bankSlipUrl} label=${text('openBankSlip')} icon="IconFileText" />
        </div>
        ${customer ? html`<h3 class="admin-dialog-subtitle">${text('customerTitle')}</h3>
          <${DefinitionList} columns=${2} items=${[
            { label: text('customerName'), value: customer.name },
            { label: text('customerDocument'), value: formatTaxDocument(customer.cpfCnpj) },
            { label: text('customerEmail'), value: customer.email || '-' },
            { label: text('customerPhone'), value: customer.mobilePhone || customer.phone || '-' },
            { label: text('customerAddress'), value: [customer.address, customer.addressNumber, customer.complement, customer.province, customer.cityName, customer.state].filter(Boolean).join(', ') || '-' },
            { label: text('customerPostalCode'), value: customer.postalCode || '-' },
          ]} />` : null}
        <h3 class="admin-dialog-subtitle">${text('invoicesTitle')}</h3>
        ${detail.invoices.length === 0 ? html`<p class="admin-muted">${text('noInvoices')}</p>` : html`<ul class="admin-feed">
          ${detail.invoices.map((/** @type {any} */ invoice) => html`<li key=${invoice.id} class="admin-feed-item">
            <span class="admin-feed-main"><span>${invoice.number ? text('invoiceNumbered', { number: invoice.number }) : invoice.id}</span><span class="settings-table-muted">${formatDay(invoice.effectiveDate)} · ${formatMoney(invoice.value)}</span></span>
            <span class="admin-feed-side"><${InvoiceStatusTag} status=${invoice.status} /><${ExternalLink} href=${invoice.pdfUrl} label="PDF" icon="IconDownload" /></span>
          </li>`)}
        </ul>`}
        <${FormError} message=${error} />
        <div class="admin-dialog-actions">
          ${canInvoicePayment(payment) ? html`<${Button} title=${text('scheduleInvoice')} icon="IconFileText" variant="secondary" size="small" onClick=${() => setMode('invoice')} />` : null}
          ${canRefundPayment(payment) ? html`<${Button} title=${text('refund')} icon="IconReceiptRefund" variant="secondary" accent="danger" size="small" onClick=${() => setMode('refund')} />` : null}
          ${canDeletePayment(payment) ? html`<${Button} title=${text('delete')} icon="IconTrash" variant="secondary" accent="danger" size="small" onClick=${() => setMode('delete')} />` : null}
        </div>
      </div>
    `}
  </${Modal}>`;
}
